class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    window.CartFreeShipping?.refreshAll();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.CartFreeShipping = window.CartFreeShipping || (() => {
  const zeroDecimalCurrencies = new Set([
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'ISK',
    'JPY',
    'KMF',
    'KRW',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
  ]);

  function normalizeCountryCode(code) {
    const normalizedCode = (code || '').trim().toUpperCase();
    return normalizedCode === 'UK' ? 'GB' : normalizedCode;
  }

  function parseThresholds(config) {
    return (config || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .reduce((thresholds, line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex === -1) return thresholds;

        const countryCode = normalizeCountryCode(line.slice(0, separatorIndex));
        const threshold = Number(line.slice(separatorIndex + 1).trim());
        if (countryCode && Number.isFinite(threshold)) thresholds[countryCode] = threshold;
        return thresholds;
      }, {});
  }

  function getThresholdConfig(container) {
    const configScript = container.querySelector('[data-cart-free-shipping-thresholds]');
    if (!configScript) return '';

    try {
      return JSON.parse(configScript.textContent);
    } catch (error) {
      return configScript.textContent;
    }
  }

  function getRate(container) {
    const currencyIso = container.dataset.currencyIso;
    const shopCurrency = container.dataset.shopCurrency;

    if (currencyIso === 'USD') return 1;

    const shopifyCurrency = window.Shopify?.currency;
    const activeCurrency = shopifyCurrency?.active || currencyIso;
    const rate = Number(shopifyCurrency?.rate);

    if (shopCurrency === 'USD' && activeCurrency === currencyIso && Number.isFinite(rate) && rate > 0) {
      return rate;
    }

    return 1;
  }

  function getMinorUnit(currencyIso) {
    return zeroDecimalCurrencies.has(currencyIso) ? 1 : 100;
  }

  function formatMoney(amount, currencyIso) {
    const maximumFractionDigits = zeroDecimalCurrencies.has(currencyIso) ? 0 : 2;

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyIso,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: maximumFractionDigits,
      maximumFractionDigits,
    }).format(amount);
  }

  function refresh(container) {
    const countryCode = normalizeCountryCode(container.dataset.countryCode);
    const currencyIso = container.dataset.currencyIso || 'USD';
    const thresholds = parseThresholds(getThresholdConfig(container));
    const defaultThresholdUsd = Number(container.dataset.defaultThresholdUsd) || 50;
    const thresholdUsd = thresholds[countryCode] ?? thresholds.DEFAULT ?? defaultThresholdUsd;
    const convertedThreshold = Math.ceil(thresholdUsd * getRate(container));
    const minorUnit = getMinorUnit(currencyIso);
    const cartTotal = Number(container.dataset.cartTotal) || 0;
    const cartTotalMajor = cartTotal / minorUnit;
    const remaining = Math.max(convertedThreshold - cartTotalMajor, 0);
    const progress = convertedThreshold > 0 ? Math.min((cartTotalMajor / convertedThreshold) * 100, 100) : 100;
    const message = container.querySelector('[data-cart-free-shipping-message]');
    const remainingTarget = container.querySelector('[data-cart-free-shipping-remaining]');
    const progressTarget = container.querySelector('[data-cart-free-shipping-progress]');
    const fillTarget = container.querySelector('.cart-free-shipping__fill');
    const thresholdInput = container.querySelector('[data-cart-free-shipping-threshold-input]');
    const isComplete = remaining <= 0;
    const progressWidth = `${progress}%`;
    const progressColor = isComplete ? '#007832' : '#17bc70';
    const trackColor = '#e6f6ef';

    container.style.setProperty('--cart-free-shipping-progress', progressWidth);
    if (fillTarget) fillTarget.style.width = progressWidth;
    if (progressTarget) {
      progressTarget.style.background = `linear-gradient(90deg, ${progressColor} 0%, ${progressColor} ${progressWidth}, ${trackColor} ${progressWidth}, ${trackColor} 100%)`;
    }
    container.classList.toggle('is-complete', isComplete);
    if (progressTarget) progressTarget.setAttribute('aria-valuenow', String(Math.round(progress)));
    if (thresholdInput) thresholdInput.value = `${currencyIso}:${convertedThreshold}`;

    if (!message || !remainingTarget) return;

    if (isComplete) {
      message.innerHTML = '<strong>Free Shipping unlocked!</strong>';
      return;
    }

    remainingTarget.textContent = formatMoney(remaining, currencyIso);
    message.replaceChildren(remainingTarget, ' away from Free Shipping');
  }

  function refreshAll(root = document) {
    root.querySelectorAll('[data-cart-free-shipping]').forEach(refresh);
  }

  function closeMenus(root = document) {
    root.querySelectorAll('[data-cart-country-toggle][aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const menu = document.getElementById(button.getAttribute('aria-controls'));
      if (menu) menu.hidden = true;
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-country-toggle]');
    if (toggle) {
      const menu = document.getElementById(toggle.getAttribute('aria-controls'));
      const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';

      closeMenus();
      toggle.setAttribute('aria-expanded', String(shouldOpen));
      if (menu) menu.hidden = !shouldOpen;
      return;
    }

    const option = event.target.closest('[data-cart-country-option]');
    if (option) {
      const form = option.closest('form');
      const countryInput = form?.querySelector('input[name="country_code"]');

      if (countryInput) countryInput.value = option.dataset.countryCode;
      form?.submit();
      return;
    }

    if (!event.target.closest('.cart-free-shipping__country')) closeMenus();
  });

  document.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') closeMenus();
  });

  document.addEventListener('DOMContentLoaded', () => refreshAll());

  return {
    refresh,
    refreshAll,
  };
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
